<script setup>
    const dayjs = useDayjs();
    import { parseTime } from '~~/shared/utilities/time';
    import { useBrowserListener } from '@/composables/browser/listeners';
    import { getConferenceTimezone, getConferenceTimezoneLabel } from '~~/shared/utilities/schedule';
    import { compareLocations, MY_EVENTS_ID } from '~~/shared/utilities/schedule/location-order';
    import { findGaps } from '#shared/utilities/schedule/find-gaps';
    import { gapRowRange } from '#shared/utilities/schedule/gap-rows';
    import { packSessionsIntoLanes } from '~~/shared/utilities/schedule/overlap';
    import AgendaCalendarCard from '~/components/blocks/two-six/agenda/calendar-card.vue';

    const emit = defineEmits(['findGap', 'findAll']);

    const props = defineProps({
        data: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        day: {
            type: String,
            default: '',
        },
        start_time: {
            type: String,
            default: '09:00',
        },
        end_time: {
            type: String,
            default: '20:00',
        },
        groupby: {
            type: String,
            default: 'location',
        },
        year: {
            type: Number,
            default: 2026,
        },
        // When false (time slider at full extent) the grid may extend past the
        // filter end to fit late sessions; when true it caps at the filter end.
        timeFiltered: {
            type: Boolean,
            default: true,
        },
        // True when EITHER time handle has been moved. `timeFiltered` only tracks
        // the end handle, so it can't answer "has the user chosen a window at
        // all?" — which is what decides whether the grid may close down to the
        // results. Defaults true so an un-passed prop keeps the old full-extent
        // grid rather than silently compressing.
        timeWindowed: {
            type: Boolean,
            default: true,
        },
        // Gap blocks ("Find a Session") only render on the My Schedule tab —
        // the All Sessions / list views have no notion of "my open time".
        myScheduleTab: {
            type: Boolean,
            default: false,
        },
        // The `conflicts` filter is active: the grid shows ONLY the clashing
        // items, so it closes down to the span they occupy instead of drawing
        // the whole programming day around them (Jack, 2026-08-20).
        conflictsOnly: {
            type: Boolean,
            default: false,
        },
    });

    const tz       = computed(() => getConferenceTimezone(props.year));
    const tzLabel  = computed(() => getConferenceTimezoneLabel(props.year));

    // ─── Schedule grouping ───────────────────────────────────────────────────────

    const week_schedule = computed(() => {
        if (!props.data?.sessions || props.groupby === 'location') return [];
        const grouped = {};

        regularSessions.value.forEach((session) => {
            const date = dayjs(session.start_date_and_time).tz(tz.value);
            const dayKey = date.format('ddd, MMM D, YYYY');
            if (!grouped[dayKey]) grouped[dayKey] = { name: dayKey, day: date.toISOString(), sessions: [], index: 0 };
            grouped[dayKey].sessions.push(session);
        });

        const ordered = Object.values(grouped).sort((a, b) => new Date(a.day) - new Date(b.day));

        ordered.forEach((group) => {
            group.sessions.sort((a, b) => new Date(a.start_date_and_time) - new Date(b.start_date_and_time));
        });

        ordered.forEach((group, idx) => {
            group.index = idx + 1;
        });

        return ordered;
    });

    // Sessions excluding all-day events (those render in their own top bar and
    // shouldn't appear in the time grid or influence its bounds).
    const regularSessions = computed(() =>
        (props.data?.sessions || []).filter(s => !s.is_all_day)
    );

    // All-day events for the currently-selected day. Excluded from the
    // time-grid bounds; rendered as a full-width bar at the top of the calendar.
    const allDaySessions = computed(() => {
        const sessions = (props.data?.sessions || []).filter(s => s.is_all_day && s.start_date_and_time);
        if (props.day === 'all') return sessions;
        return sessions.filter(s => {
            const laDate = new Intl.DateTimeFormat('en-CA', { timeZone: tz.value })
                .format(new Date(s.start_date_and_time))
                .slice(0, 10);
            return laDate === props.day;
        });
    });

    const formatAllDayTime = (session) => {
        if (!session.start_date_and_time) return '';
        const start = dayjs(session.start_date_and_time).tz(tz.value);
        const end = session.end_date_and_time
            ? dayjs(session.end_date_and_time).tz(tz.value)
            : null;
        const startStr = start.format('h:mma');
        const endStr = end ? end.format('h:mma') : '';
        return end ? `${startStr} - ${endStr} ${tzLabel.value}` : `${startStr} ${tzLabel.value}`;
    };

    const location_schedule = computed(() => {
        if (!props.data?.sessions || props.groupby !== 'location') return [];
        const grouped = {};

        regularSessions.value.forEach((session) => {
            // Personal (custom) events have no room/stage, so they'd never land
            // in a location column. Give them a dedicated track; compareLocations
            // pins it ahead of every real location by its id.
            if (session.is_my_event) {
                if (!grouped[MY_EVENTS_ID]) grouped[MY_EVENTS_ID] = { name: 'My Events', location: { id: MY_EVENTS_ID, name: 'My Events' }, sessions: [], index: 0 };
                grouped[MY_EVENTS_ID]['sessions'].push(session);
                return;
            }
            if (Array.isArray(session.locations)) {
                session.locations.forEach((location) => {
                    if (!grouped[location.id]) grouped[location.id] = { name: location.name, location: location, sessions: [], index: 0 };
                    grouped[location.id]['sessions'].push(session);
                });
            }
        });

        grouped._ordered = Object.values(grouped).sort(compareLocations);

        Object.keys(grouped._ordered).forEach((location) => {
            grouped._ordered[location]['sessions'].sort((a, b) => new Date(a.start_date_and_time) - new Date(b.start_date_and_time));
        });

        let counter = 0;
        Object.keys(grouped._ordered).forEach((location) => {
            grouped._ordered[location]['index'] = ++counter;
        });

        return grouped._ordered;
    });

    // My Schedule collapses to a SINGLE chronological column, and only grows a
    // second (or third) when the day's timed sessions actually conflict (Jack,
    // 2026-07-10). Columns are LANES, not rooms: a personal schedule doesn't
    // need a column per location, so everything that fits chronologically stays
    // in lane one and only the conflicting session moves right.
    //
    // This replaced a fallback to the per-location grouping, which produced a
    // column for every room the moment ANY two sessions overlapped — a day with
    // one conflict across eight rooms rendered eight columns of mostly single
    // cards, unreadable on a phone.
    //
    // Blank name/location so no single room label is implied (the room isn't
    // meaningful across a merged column); groupKey falls back to the stable id.
    const laneColumns = computed(() => {
        // An all-day-only day packs to zero lanes; keep the one empty column the
        // single-column version always produced so the track still renders.
        const lanes = packSessionsIntoLanes(regularSessions.value);
        if (!lanes.length) lanes.push([]);
        return lanes.map((sessions, i) => ({
            name: '',
            location: { id: i === 0 ? 'my-schedule-single' : `my-schedule-lane-${i + 1}`, name: '' },
            sessions,
            index: i + 1,
        }));
    });

    const schedule = computed(() => {
        if (props.myScheduleTab) return laneColumns.value;
        return props.groupby === 'location' ? location_schedule.value : week_schedule.value;
    });

    // Stable v-for key for a column. `group.index` and the array position are both
    // POSITIONAL — removing the last session in a room drops that column and
    // renumbers every column after it, so keying on either makes Vue tear down and
    // rebuild those columns (and their cards) on an unrelated removal.
    const groupKey = (group) => group.location?.id ?? group.name;

    // ─── Time blocks ─────────────────────────────────────────────────────────────

    // Calendar bounds are derived from the actual data so the grid always fits
    // every session in the result set. Earliest start (floored to the hour) and
    // latest end (ceiled to the hour) drive both the time-gutter rows and each
    // card's row position. Falls back to the props if no sessions are present.
    const data_bounds = computed(() => {
        const sessions = regularSessions.value;

        // Parse props bounds — grid always covers at least the user's selected range.
        const [propsSh, propsSm] = props.start_time ? parseTime(props.start_time) : [9, 0];
        const [propsEh, propsEm] = props.end_time   ? parseTime(props.end_time)   : [20, 0];
        const propsStartMin = propsSh * 60 + propsSm;
        const propsEndMin   = propsEh  * 60 + propsEm;

        if (sessions.length === 0) {
            return { startHour: propsSh, startMinute: propsSm, endHour: propsEh, endMinute: propsEm };
        }

        let minStart = Infinity;
        let maxEnd = -Infinity;
        for (const s of sessions) {
            if (!s.start_date_and_time) continue;
            const start = dayjs(s.start_date_and_time).tz(tz.value);
            const end = s.end_date_and_time
                ? dayjs(s.end_date_and_time).tz(tz.value)
                : start.add(1, 'hour');
            const startMin = start.hour() * 60 + start.minute();
            // Cap end at end-of-day for sessions that cross midnight — the grid
            // is one-day-wide, so we don't try to render past 24:00.
            // Cross-midnight sessions are clamped to 23:59 so the grid never has
            // to render a 24th-hour row (dayjs.hour(24) normalizes to next-day
            // midnight, which collapses the grid template).
            let endMin = end.isSame(start, 'day')
                ? end.hour() * 60 + end.minute()
                : 23 * 60 + 59;
            if (endMin <= startMin) endMin = Math.min(startMin + 60, 23 * 60 + 59);
            if (startMin < minStart) minStart = startMin;
            if (endMin > maxEnd) maxEnd = endMin;
        }
        if (minStart === Infinity || maxEnd === -Infinity) {
            return { startHour: propsSh, startMinute: propsSm, endHour: propsEh, endMinute: propsEm };
        }

        // Always snap to whole hours.
        //
        // With a time window chosen the grid spans it, even where it's empty —
        // the selected range IS the thing being looked at, so showing its full
        // extent is the point (and the grid still starts at the floor of the
        // user's start, even when the first session begins later).
        //
        // With both handles at full extent there's no such intent, and padding
        // out to the whole day is just dead space: filtering My Schedule down to
        // one 30-minute custom event drew a ~3,800px grid of empty hours around
        // it (UNB-634). So the grid closes down to the results themselves.
        // My Schedule is the exception to that compression. There the empty
        // stretches ARE the content — they're where the Find a Session gap blocks
        // are drawn, so closing the grid down to the sessions the viewer already
        // has hides the open time they came to fill. So the tab spans the day's
        // programming window (the props bounds, which sit at the day window when
        // no handle has moved) even with no time window chosen. This supersedes
        // UNB-634's compression for My Schedule only; All Sessions still closes
        // down to its results.
        // Showing only conflicts overrides both of the reasons the grid would
        // otherwise stay open: the My Schedule day-span (there is no open time
        // to fill here — the task is resolving an overlap, and the gap blocks
        // are suppressed to match) and any time window still set on the slider.
        const spansDay = !props.conflictsOnly && (props.myScheduleTab || props.timeWindowed);
        const effectiveStart = spansDay ? Math.min(minStart, propsStartMin) : minStart;
        // End cap still keyed to `timeFiltered` (the end handle) — that's the one
        // that must never be exceeded. Unwindowed, `maxEnd` alone already covers
        // late events like the VIP Party; it was taking the max with the prop end
        // that held the grid open to the end of the day.
        const effectiveEnd = (props.timeFiltered && !props.conflictsOnly)
            ? propsEndMin
            : (spansDay ? Math.max(maxEnd, propsEndMin) : maxEnd);

        // Ceil to the hour: a session ending mid-hour needs its whole row, but one
        // ending exactly on the hour must NOT add an empty trailing row (a day whose
        // last session ends 7:00pm should stop at the 7pm line, not render an empty
        // 7-8pm block). floor(x)+1 over-counted on exact hours; Math.ceil doesn't.
        const rawEndHour = Math.ceil(effectiveEnd / 60);
        const capped     = rawEndHour > 23;
        return {
            startHour: Math.max(0, Math.floor(effectiveStart / 60)),
            startMinute: 0,
            endHour:   Math.min(23, rawEndHour),
            endMinute: capped ? 59 : 0,
        };
    });

    // Convert an ISO session start time to a grid row (header row counts as 1).
    // Overrides the endpoint's pre-computed `grid_row_start` so the row math
    // stays correct after data_bounds shifts.
    const baseStartMinutes = computed(() =>
        data_bounds.value.startHour * 60 + data_bounds.value.startMinute
    );
    const sessionGridStart = (session) => {
        // +3 (not +2): row 1 = stage headers, row 2 = all-day events row,
        // rows 3..N+2 = time slots.
        if (!session.start_date_and_time) return 3;
        const start = dayjs(session.start_date_and_time).tz(tz.value);
        const sessionStartMin = start.hour() * 60 + start.minute();
        const offset = Math.max(0, sessionStartMin - baseStartMinutes.value);
        return Math.floor(offset / 15) + 3;
    };

    // Recompute span client-side so cross-midnight sessions don't overflow the
    // one-day grid. The endpoint's pre-computed grid_row_span counts the full
    // duration which can be 51+ rows for an overnight after-party.
    const sessionGridSpan = (session) => {
        if (!session.start_date_and_time) return Math.max(1, session.grid_row_span || 1);
        const start = dayjs(session.start_date_and_time).tz(tz.value);
        const end = session.end_date_and_time
            ? dayjs(session.end_date_and_time).tz(tz.value)
            : start.add(1, 'hour');
        const startMin = start.hour() * 60 + start.minute();
        let endMin = end.isSame(start, 'day')
            ? end.hour() * 60 + end.minute()
            : 23 * 60 + 59;
        if (endMin <= startMin) endMin = Math.min(startMin + 60, 23 * 60 + 59);
        return Math.max(1, Math.ceil((endMin - startMin) / 15));
    };

    // ─── Gap blocks ("Find a Session") ─────────────────────────────────────────
    // Calendar view + My Schedule tab only (spec decision 17). One block per
    // open stretch >= MIN_GAP_MINUTES on the visible day, spanning every
    // location column. `findGaps` alone decides whether a day gets blocks at
    // all (its "empty day" carve-out returns [] with nothing scheduled) — no
    // extra day-emptiness check needed here.
    const gaps = computed(() => {
        if (!props.myScheduleTab || props.day === 'all') return [];
        // Not while showing only conflicts: day_gaps describes the REAL
        // schedule's open time, which has nothing to do with the overlap being
        // resolved and would paint blocks across a grid narrowed to exclude it.
        if (props.conflictsOnly) return [];

        // Prefer the server's gaps. They are computed from the viewer's REAL
        // schedule, using the same busy intervals and clamp POST /schedule/suggest
        // validates against, so what we draw is what suggest will accept.
        //
        // Deriving them from `sessions` here was wrong: that list is filtered.
        // Narrowing the time slider dropped scheduled sessions from the feed, so
        // this saw free time where the user actually had sessions, drew a block
        // over them, and the button then 400d because the gap it posted did not
        // exist in the real schedule (UNB).
        const serverGaps = props.data?.day_gaps;
        if (Array.isArray(serverGaps)) {
            return serverGaps.map((g) => ({
                startDateTime: new Date(g.start),
                endDateTime: new Date(g.end),
            }));
        }

        // Fallback for a logged-out or older response: same clamp as before, so a
        // trailing gap is not offered past the last session that could fill it.
        return findGaps(props.data?.sessions || [], props.day, tz.value, props.data?.day_bounds || null);
    });

    // Gaps we will actually draw. A gap has to earn its block twice: enough
    // DURATION to be worth filling (findGaps, MIN_GAP_MINUTES) and enough ROOM
    // on screen to show the block at all.
    const renderableGaps = computed(() => gaps.value.filter((gap) => {
        // One 15-minute row can never hold it, whatever the row height.
        if (gapGridSpan(gap) < 2) return false;
        const height = gapBlockHeightPx(gap);
        // 0 means the grid has not been measured yet, on first paint. Keep it for
        // now rather than flashing the block out; this recomputes once the row
        // heights land.
        return height === 0 || height >= MIN_GAP_BLOCK_PX;
    }));

    // Row geometry lives in gapRowRange (shared) — the +3 here is this grid's own
    // header offset: row 1 = stage headers, row 2 = all-day events.
    const gapRows = (gap) => gapRowRange(gap.startDateTime, gap.endDateTime, baseStartMinutes.value, tz.value);
    const gapGridStart = (gap) => gapRows(gap).startRow + 3;
    const gapGridSpan = (gap) => gapRows(gap).span;

    // A gap of an hour or less keeps its text + buttons centered and drops the
    // sticky behaviour (Asana 687639, third pass): in a band that short the
    // sticky travel is only a few px, so the CTA appears to twitch as you scroll
    // rather than track — Nick's "the area is so small it just feels weird". The
    // `.is-short` class swaps the block to centered + a static inner (see CSS).
    const SHORT_GAP_MAX_MINUTES = 60;
    const isShortGap = (gap) => {
        const start = dayjs(gap.startDateTime).tz(tz.value);
        const end = dayjs(gap.endDateTime).tz(tz.value);
        const minutes = end.isSame(start, 'day')
            ? end.diff(start, 'minute')
            : (23 * 60 + 59) - (start.hour() * 60 + start.minute());
        return minutes <= SHORT_GAP_MAX_MINUTES;
    };

    const emitFindGap = (gap) => {
        emit('findGap', {
            date: props.day,
            start: gap.startDateTime.toISOString(),
            end: gap.endDateTime.toISOString(),
        });
    };

    const time_blocks = computed(() => {
        const { startHour, startMinute, endHour, endMinute } = data_bounds.value;

        const today = dayjs(props.day).format('YYYY-MM-DD');
        let start = dayjs(today).hour(startHour).minute(startMinute).second(0).millisecond(0);
        let end = dayjs(today).hour(endHour).minute(endMinute).second(0).millisecond(0);

        start = start.minute(0);
        if (end.minute() !== 0) end = end.add(1, 'hour').minute(0);

        const blocks = [];

        for (let time = start; time.isBefore(end); time = time.add(15, 'minutes')) {
            blocks.push({
                hour: time.hour(),
                minute: time.minute(),
                label: time.minute() === 0 ? `${time.format('h:mma')} ${tzLabel.value}` : '',
                mobileLabel: time.minute() === 0 ? `${time.format('ha')} ${tzLabel.value}` : '',
                start: time.toISOString(),
                end: time.add(15, 'minutes').toISOString(),
            });
        }

        return blocks;
    });

    // Single-day: only cards whose start is within the visible time window.
    const visibleGroupSessions = (sessions) =>
        (sessions || []).filter((s) => sessionGridStart(s) <= time_blocks.value.length + 2);

    // ─── Scroll / sticky controls ─────────────────────────────────────────────────

    const schedule_grid = ref(null);
    const gridCanGoLeft = ref(false);
    const gridCanGoRight = ref(false);
    // "The columns don't all fit." True at any scroll position when there is
    // overflow — fully scrolled right still counts, via gridCanGoLeft.
    const gridScrollable = computed(() => gridCanGoLeft.value || gridCanGoRight.value);
    // Whether the grid bleeds to the viewport's right edge. Only true when the
    // columns can't fit at their minimum width (i.e. the grid scrolls); when
    // they fit, the grid stays inside the component's right padding.
    const bleeding = ref(false);
    const headers = ref(null);
    const sticky = ref(null);
    const sticky_active = ref(false);
    // Top offset for the sticky header — follows the 2026 site header so the
    // two never overlap (the old --header-height var belongs to the retired
    // global header and isn't set on the 2026 site).
    const sticky_top = ref(0);
    // Left + width overlay the calendar grid exactly (it bleeds to the right
    // viewport edge, so the sticky can't be a centered fixed-width box).
    const sticky_left = ref(0);
    const sticky_width = ref(0);
    // Width pinned to the tracks scroll container's own clientWidth (the
    // visible viewport area, excluding the fixed time gutter). The gap block
    // is `position: sticky; left: 0` inside that same scrolling container, so
    // giving it this width — instead of the full (scrollable) grid-track
    // width its `grid-column: 1 / -1` placement would otherwise render at —
    // is what makes it appear pinned to the viewport as the grid scrolls
    // horizontally, rather than a 1210px-wide box the user has to scroll to
    // see the middle of.
    // Find a Session gap-block geometry. The block spans the calendar's padded
    // content box — screen width minus the box padding on both sides — INCLUDING
    // the time-gutter column, not just the scrolling tracks area. It can't do
    // that from inside the tracks grid (overflow-x: scroll clips anything left of
    // the gutter), so it's rendered as an absolute overlay on
    // `.agenda-calendar-body` (gutter + tracks) and positioned vertically from
    // the grid's resolved row heights.
    const gap_block_width = ref(0);
    const gap_block_left = ref(0);
    const row_heights = ref([]);

    const syncStickyGeometry = () => {
        const cal = sticky.value?.closest('.agenda-calendar');
        if (cal) {
            const rect = cal.getBoundingClientRect();
            sticky_left.value = Math.round(rect.left);
            sticky_width.value = Math.round(rect.width);
        }
    };

    // Measured after layout (row heights include the `auto` all-day row, which
    // pure CSS-constant math can't know). Only depends on layout, not on the
    // tracks' horizontal scroll position, so it runs from refreshLayout (mount /
    // resize / data change), not the scroll handler.
    const syncGapBlocks = () => {
        const grid = schedule_grid.value;
        if (!grid) return;
        row_heights.value = getComputedStyle(grid).gridTemplateRows
            .split(' ')
            .map((v) => parseFloat(v) || 0);
        const body = grid.closest('.agenda-calendar-body');
        const gutter = body?.querySelector('.agenda-calendar-timeline');
        const gutterWidth = gutter ? gutter.getBoundingClientRect().width : 0;
        // Inset by the session cards' own horizontal margin so the block lines up
        // with the cards (which sit `margin: 0.0625rem 0.5rem` inside their
        // column) rather than flush against the column edge. Measured off a real
        // card so it can't drift from the CSS; 8px (0.5rem) fallback when the day
        // has no cards.
        const card = grid.querySelector('.agenda-cal-card');
        const cardInset = card ? (parseFloat(getComputedStyle(card).marginLeft) || 0) : 8;
        // Start after the time-gutter column and span the rest of the padded
        // content box (content width minus the time column, minus the right
        // bleed so it respects the right padding rather than running to the
        // viewport edge like the scrolling columns do), then inset both sides by
        // the card margin.
        const bodyWidth = body ? body.getBoundingClientRect().width : grid.clientWidth;
        const contentWidth = bodyWidth - (bleeding.value ? getBleedPx() : 0);
        gap_block_left.value = Math.round(gutterWidth + cardInset);
        gap_block_width.value = Math.round(contentWidth - gutterWidth - 2 * cardInset);
    };

    // Inline style for one gap block: vertical extent from the grid rows it
    // covers, horizontal extent from the padded content box.
    // Rendered height of a gap block, in px, from the rows it covers.
    const gapBlockHeightPx = (gap) => {
        const start = gapGridStart(gap);
        const span = gapGridSpan(gap);
        const rows = row_heights.value;
        let height = 0;
        for (let i = start - 1; i < start - 1 + span && i < rows.length; i++) height += rows[i];
        return height;
    };

    // The block's content — title, subhead, two buttons — needs about 150px, and
    // the 80px top/bottom padding that frames a tall gap adds 160px more. A short
    // block can be a lot less than that: a 45-minute gap that starts mid-row
    // covers only two 15-minute rows once its start is rounded up off the session
    // above and its end rounded down off the session below, which is ~210px. The
    // content then spilled out of the block and over the row beneath it.
    //
    // Below this threshold the block drops to tight padding instead (.is-tight).
    // Deliberately generous: at 3 rows the full-padding layout clears by only a
    // few px, so a subhead that wraps to two lines on a narrower column would
    // overflow as well.
    // Below this even the tight layout cannot fit the title, subhead and both
    // buttons, so the block is not worth offering — it would be an affordance
    // with no room to be used.
    //
    // A gap can be long enough to qualify by DURATION and still render tiny: the
    // rows are measured against the visible grid, so a gap that begins before the
    // grid does — the time slider having moved the window forward — is clamped to
    // the first row and only its tail is drawn. A 45-minute gap can end up one
    // 15-minute row tall that way, which is where this was showing up (UNB).
    const MIN_GAP_BLOCK_PX = 180;

    const TIGHT_GAP_MAX_PX = 360;
    const isTightGap = (gap) => {
        const h = gapBlockHeightPx(gap);
        // 0 before the grid has been measured — don't flag tight on first paint.
        return h > 0 && h < TIGHT_GAP_MAX_PX;
    };

    const gapBlockStyle = (gap) => {
        const start = gapGridStart(gap);
        const rows = row_heights.value;
        let top = 0;
        for (let i = 0; i < start - 1 && i < rows.length; i++) top += rows[i];
        const height = gapBlockHeightPx(gap);
        // Inset so the block sits FLUSH against the 15-minute dividers with no pink
        // gap either side, matching the session cards (Asana 687600). The divider
        // is a 1px line painted in the FIRST pixel of each row, so +1 drops the
        // block's top stroke just past its top-boundary line (line shows, block
        // flush below it), and -1 pulls the bottom flush against the next line.
        // Net +1 / -1. Earlier rounds used +2/-3, which left a designed 1px of pink
        // each side — the gap Nick kept flagging.
        return {
            top: `${Math.round(top) + 1}px`,
            height: `${Math.max(0, Math.round(height) - 1)}px`,
            left: `${gap_block_left.value}px`,
            width: `${gap_block_width.value}px`,
        };
    };

    const getColWidthPx = () => {
        if (!schedule_grid.value) return 240;
        const raw = getComputedStyle(schedule_grid.value).getPropertyValue('--column-width').trim();
        if (raw.endsWith('rem')) return parseFloat(raw) * 16;
        if (raw.endsWith('px'))  return parseFloat(raw);
        return 240;
    };

    const scrollToLeft = () => {
        if (schedule_grid.value) {
            const el = schedule_grid.value;
            const colWidth = getColWidthPx();
            const visibleCols = Math.max(1, Math.floor(el.clientWidth / colWidth));
            el.scrollTo({ left: el.scrollLeft - visibleCols * colWidth, behavior: 'smooth' });
        }
    };

    const scrollToRight = () => {
        if (schedule_grid.value) {
            const el = schedule_grid.value;
            const colWidth = getColWidthPx();
            const visibleCols = Math.max(1, Math.floor(el.clientWidth / colWidth));
            el.scrollTo({ left: el.scrollLeft + visibleCols * colWidth, behavior: 'smooth' });
        }
    };

    const handleScroll = () => {
        if (schedule_grid.value) {
            gridCanGoLeft.value = schedule_grid.value.scrollLeft > 0;
            gridCanGoRight.value = schedule_grid.value.scrollLeft < schedule_grid.value.scrollWidth - schedule_grid.value.clientWidth;
        }
        if (headers.value && schedule_grid.value) {
            headers.value.scrollLeft = schedule_grid.value.scrollLeft;
        }
    };

    // ── Right-edge bleed (only when the grid overflows) ──────────────────────
    const getBleedPx = () => {
        if (!schedule_grid.value) return 0;
        const raw = getComputedStyle(schedule_grid.value)
            .getPropertyValue(window.innerWidth < 768 ? '--box-padding-lr-m' : '--box-padding-lr').trim();
        if (raw.endsWith('rem')) return parseFloat(raw) * 16;
        if (raw.endsWith('px'))  return parseFloat(raw);
        return parseFloat(raw) || 0;
    };

    // Bleed the grid to the viewport's right edge ONLY when the columns can't fit
    // at their minimum width within the padded content box (the grid will
    // scroll). When they fit, stay inside the component's right padding instead
    // of stretching to the window edge. Measured margin-independently — subtract
    // whatever bleed is currently applied — so toggling it can't oscillate at
    // the boundary.
    const updateBleed = () => {
        const el = schedule_grid.value;
        if (!el) return;
        const paddedWidth = el.clientWidth - (bleeding.value ? getBleedPx() : 0);
        const needed = Math.max(schedule.value.length, 1) * getColWidthPx();
        bleeding.value = needed > paddedWidth + 1;
    };

    const refreshLayout = () => {
        updateBleed();
        nextTick(() => { handleScroll(); syncStickyGeometry(); syncGapBlocks(); });
    };

    const onWindowScroll = () => {
        if (!schedule_grid.value || !headers.value) return;
        const gridTop = schedule_grid.value.getBoundingClientRect().top + window.scrollY;
        const scrolled = window.scrollY > gridTop + 50;
        // Only worth pinning when the columns actually overflow. Where they all fit
        // there is nothing to scroll sideways to, so the bar carries only the
        // carousel arrows — which are themselves gated on the same condition — and
        // My Schedule hides the stage names, leaving an empty strip pinned over the
        // calendar. `gridScrollable` reads both directions rather than scrollWidth
        // directly so it agrees with the arrows at every scroll position: fully
        // scrolled right still counts as scrollable via gridCanGoLeft.
        sticky_active.value = scrolled && gridScrollable.value;
        // Tuck the sticky header under the 2026 site nav while it's visible
        // (scroll-up); when the nav slides away (scroll-down) the calendar
        // header takes the very top. Keyed off the nav's state class + its
        // stable layout height rather than its live (mid-slide) rect, so a
        // direction flip can't momentarily read 0 and flash an overlap.
        const nav = document.querySelector('nav.block-2026-header');
        sticky_top.value = nav?.classList.contains('header-visible') ? nav.offsetHeight : 0;
        syncStickyGeometry();
    };

    onMounted(() => {
        if (schedule_grid.value && process.client) {
            window.addEventListener('scroll', onWindowScroll, { passive: true });
            schedule_grid.value.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();
            nextTick(refreshLayout);
        }
    });

    useBrowserListener('resize', () => {
        if (schedule_grid.value && process.client) {
            refreshLayout();
        }
    });

    // Column count / day changes alter how much width the grid needs — re-decide
    // whether it should bleed after the new layout renders.
    //
    // The ROW count belongs here too, and its absence is what UNB-732 was. Every
    // measured value this component holds — row_heights, the gap blocks' vertical
    // extent, the bleed decision — is read off the laid-out grid by refreshLayout,
    // and the grid's row count moves whenever a filter changes the derived bounds.
    // Clearing "Conflicts Only" does exactly that: it widens the session set, so
    // the grid grows hours. On a day whose clash already forces two lanes the
    // COLUMN count is identical either side of the toggle, so nothing above could
    // see it, and the restored Find a Session blocks were positioned against row
    // heights measured for the narrowed grid (Shannon, Asana 1217792040616413).
    watch([() => schedule.value.length, () => props.day, () => time_blocks.value.length], () => {
        if (process.client) nextTick(refreshLayout);
    });

    onBeforeUnmount(() => {
        if (schedule_grid.value && process.client) schedule_grid.value.removeEventListener('scroll', handleScroll);
        if (process.client) window.removeEventListener('scroll', onWindowScroll);
    });
</script>

<template>
    <div
        class="agenda-calendar"
        :class="{ loading: loading, day: day !== 'all', bleed: bleeding }"
        :style="`--columns: ${Math.max(schedule.length, 1)}; --rows: ${time_blocks.length}`"
    >
        <!-- Schedule wrapper -->
        <!-- `has-carousel` mirrors the v-if on the carousel controls below. The header
             row only exists to house those arrows on My Schedule (the track names are
             hidden there), so the CSS needs to know whether they are actually rendered
             — otherwise a multi-lane day reserves the row whether or not anything is
             in it. It has to be a class rather than sizing the row `auto`, because the
             timeline and the tracks are separate grids that must keep their rows
             aligned, and the arrows live only in the timeline. -->
        <div class="agenda-calendar-schedule" :class="{ loading: loading, day: day !== 'all', all: day === 'all', 'is-myschedule': myScheduleTab, 'is-single-lane': myScheduleTab && schedule.length < 2, 'has-carousel': gridCanGoLeft || gridCanGoRight }">

            <!-- Sticky header row (scrolls into view when user scrolls down past the grid header) -->
            <div class="agenda-calendar-sticky" ref="sticky" :class="{ active: sticky_active }" :inert="!sticky_active" :style="{ '--cal-sticky-top': sticky_top + 'px', '--cal-sticky-left': sticky_left + 'px', '--cal-sticky-width': sticky_width + 'px' }">
                <div class="agenda-calendar-timeline" :style="`--index: 0`">
                    <div v-if="gridCanGoLeft || gridCanGoRight" class="agenda-cal-carousel-controls">
                        <FragmentsCarouselControls
                            :has-prev="gridCanGoLeft"
                            :has-next="gridCanGoRight"
                            variant="magenta"
                            :prev-label="myScheduleTab ? 'Previous column' : 'Previous stages'"
                            :next-label="myScheduleTab ? 'Next column' : 'Next stages'"
                            @prev="scrollToLeft"
                            @next="scrollToRight" />
                    </div>
                </div>
                <ul class="agenda-calendar-tracks" ref="headers">
                    <li
                        v-for="(group, index) in schedule"
                        :key="groupKey(group)"
                        class="agenda-calendar-track"
                        :class="{ alt: index % 2 !== 0 }"
                        :style="`--index: ${group.index}`"
                    >
                        <h3 class="agenda-calendar-track-name" :style="`--index: ${group.index}`">
                            {{ group.name }}
                            <span v-if="group.location?.description" v-html="group.location.description"></span>
                        </h3>
                    </li>
                </ul>
            </div>

            <!-- Body: gutter + tracks side by side -->
            <div class="agenda-calendar-body">

            <!-- Time gutter (day mode only) -->
            <div v-if="day !== 'all'" class="agenda-calendar-timeline" :style="`--index: 1`">
                <div v-if="gridCanGoLeft || gridCanGoRight" class="agenda-cal-carousel-controls">
                    <FragmentsCarouselControls
                        :has-prev="gridCanGoLeft"
                        :has-next="gridCanGoRight"
                        variant="magenta"
                        :prev-label="myScheduleTab ? 'Previous column' : 'Previous stages'"
                        :next-label="myScheduleTab ? 'Next column' : 'Next stages'"
                        @prev="scrollToLeft"
                        @next="scrollToRight" />
                </div>
                <!-- Phantom all-day spacer: keeps timeline row 2 the same height as
                     tracks row 2 so the time labels in rows 3+ line up with their
                     corresponding session-card rows. Rendered with the same markup
                     as the visible bar so auto-sizing matches; hidden visually. -->
                <div
                    v-if="day !== 'all' && allDaySessions.length > 0"
                    class="agenda-cal-all-day agenda-cal-all-day-phantom"
                    aria-hidden="true"
                    inert>
                    <div
                        v-for="session in allDaySessions"
                        :key="`all-day-phantom-${session.id || session.unique_id}`"
                        class="agenda-cal-all-day-bar">
                        <span class="time">{{ formatAllDayTime(session) }}</span>
                        <span class="title">{{ session.title }}</span>
                    </div>
                </div>
                <template v-for="(block, index) in time_blocks" :key="block.start">
                    <span class="agenda-cal-time-label long" v-if="block.label" :style="`--starts: ${index + 3}`" inert tabindex="-1">{{ block.label }}</span>
                    <span class="agenda-cal-time-label short" v-if="block.mobileLabel" :style="`--starts: ${index + 3}`" inert tabindex="-1">{{ block.mobileLabel }}</span>
                </template>
            </div>

            <!-- Tracks grid -->
            <div class="agenda-calendar-tracks" ref="schedule_grid">
                <!-- All-day events row (spans all columns at grid row 2, between stage headers and first time slot) -->
                <div v-if="day !== 'all' && allDaySessions.length > 0" class="agenda-cal-all-day">
                    <nuxt-link
                        v-for="session in allDaySessions"
                        :key="`all-day-${session.id || session.unique_id}`"
                        :to="`/sessions/${session.unique_id || session.id}`"
                        class="agenda-cal-all-day-bar">
                        <span class="time">{{ formatAllDayTime(session) }}</span>
                        <span class="title">{{ session.title }}</span>
                    </nuxt-link>
                </div>
                <!-- All-days mode: flex columns -->
                <template v-if="day === 'all'">
                    <ul class="agenda-cal-stage-tracks">
                        <li
                            v-for="(group, index) in schedule"
                            :key="groupKey(group)"
                            class="agenda-calendar-track"
                            :class="{ alt: index % 2 !== 0 }"
                            :style="`--index: ${group.index}`"
                        >
                            <box class="agenda-cal-row-box" inert></box>
                            <h3 class="agenda-calendar-track-name" :style="`--index: ${group.index}`">
                                {{ group.name }}
                                <span v-if="group.location?.description" v-html="group.location.description"></span>
                            </h3>
                            <template v-for="session in group.sessions" :key="`card-${session.unique_id}`">
                                <AgendaCalendarCard
                                    :session="session"
                                    :year="year"
                                    :is-full="!!session.is_full"
                                    :action-required="!!session.user_added && session.session_reservations === true && !session.user_has_seat && !session.user_waitlisted"
                                    :has-saved-seat="!!session.user_has_seat"
                                    :class="{ 'session-short': session.grid_row_span === 1 }"
                                    :style="`--index: ${group.index}; --spans: ${sessionGridSpan(session)}; --starts: ${sessionGridStart(session)}`"
                                />
                            </template>
                        </li>
                    </ul>
                </template>

                <!-- Single-day mode: CSS grid -->
                <template v-else>
                    <template v-for="group in schedule" :key="groupKey(group)">
                        <box class="agenda-cal-row-box" :class="{ alt: group.index % 2 === 0 }" :style="`--index: ${group.index}`" inert></box>
                        <h3 class="agenda-calendar-track-name" :style="`--index: ${group.index}`">
                            {{ group.name }}
                            <span v-if="group.location?.description" v-html="group.location.description"></span>
                        </h3>
                        <template v-for="session in visibleGroupSessions(group.sessions)" :key="`card-${session.unique_id}`">
                            <AgendaCalendarCard
                                :session="session"
                                :year="year"
                                :is-full="!!session.is_full"
                                :action-required="!!session.user_added && session.session_reservations === true && !session.user_has_seat && !session.user_waitlisted"
                                :has-saved-seat="!!session.user_has_seat"
                                :class="{ 'session-short': session.grid_row_span === 1 }"
                                :style="`--index: ${group.index}; --spans: ${sessionGridSpan(session)}; --starts: ${sessionGridStart(session)}`"
                            />
                        </template>
                    </template>

                    <!-- Horizontal row lines (one per time block) -->
                    <template v-for="i in time_blocks.length" :key="`line-${i}`">
                        <line class="agenda-cal-row-line" :class="{ hour: i === 1 || (i - 1) % 4 === 0 }" :style="`--starts: ${i + 2}`" inert tabindex="-1"></line>
                    </template>
                </template>
            </div>

            <!-- Gap blocks: "Find a Session" (calendar view + My Schedule tab
                 only — spec decision 17). Rendered as an absolute overlay on the
                 body (gutter + tracks) rather than inside the tracks grid, so it
                 spans the calendar's full padded content width INCLUDING the time
                 gutter (the tracks grid clips anything left of the gutter). Its
                 vertical extent comes from the grid rows it covers
                 (gapBlockStyle → row_heights). -->
            <template v-if="day !== 'all'">
                    <template v-for="gap in renderableGaps" :key="`gap-${gap.startDateTime.toISOString()}`">
                        <div
                            class="agenda-cal-gap-block"
                            :class="{ 'is-short': isShortGap(gap), 'is-tight': isTightGap(gap) }"
                            data-test="gap-block"
                            :style="gapBlockStyle(gap)"
                        >
                            <!-- Sticky inner (Asana 687639): on a very tall gap the
                                 centered text + buttons would otherwise sit marooned in
                                 the middle of the open region, out of view while the user
                                 scrolls its top or bottom. Pinning the inner keeps them
                                 reachable the whole time the gap is on screen; a short
                                 gap never scrolls far enough for sticky to engage, so it
                                 still reads as centered. -->
                            <div class="agenda-cal-gap-inner">
                            <h3 class="agenda-cal-gap-title">Find a Session</h3>
                            <p class="agenda-cal-gap-subhead">We&rsquo;ll recommend a session based on your preferences</p>
                            <div class="agenda-cal-gap-actions">
                                <button type="button" class="agenda-cal-gap-btn is-filled" data-test="fill-this-slot" @click="emitFindGap(gap)">
                                    <svg viewBox="0 0 13 13" fill="none" class="agenda-cal-gap-icon" aria-hidden="true">
                                        <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                                        <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
                                    </svg>
                                    Fill This Time Slot
                                </button>
                                <button type="button" class="agenda-cal-gap-btn is-outline" data-test="fill-all-slots" @click="emit('findAll')">
                                    <svg viewBox="0 0 13 13" fill="none" class="agenda-cal-gap-icon" aria-hidden="true">
                                        <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                                        <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
                                    </svg>
                                    Fill All Open Time Slots
                                </button>
                            </div>
                            </div><!-- /.agenda-cal-gap-inner -->
                        </div>
                    </template>
            </template>

            </div><!-- /.agenda-calendar-body -->

        </div>
    </div>
</template>

<style>
    /* ── Root container ──────────────────────────────────────────────────────── */
    .agenda-calendar {
        position: relative;
    }

    /* Bleed to the viewport's right edge only while the grid overflows (so the
       scrolling columns can reach the edge). When the columns fit, no bleed —
       the grid respects the component's right padding. Toggled from JS. */
    .agenda-calendar.bleed {
        margin-right: calc(-1 * var(--box-padding-lr-m));
    }

    @media (min-width: 768px) {
        .agenda-calendar.bleed {
            margin-right: calc(-1 * var(--box-padding-lr));
        }
    }

    .agenda-calendar.loading {
        pointer-events: none;
        opacity: 0.5;
    }

    .agenda-calendar .back-to-top {
        height: 7.3125rem;
    }

    @media (min-width: 1024px) {
        .agenda-calendar .back-to-top {
            margin-bottom: -5.5rem;
        }
    }

    .agenda-calendar .back-to-top button {
        left: 0;
    }

    .agenda-calendar .back-to-top.is-fixed button,
    .agenda-calendar .back-to-top.offscreen button {
        left: var(--box-padding-l);
    }

    /* ── Schedule wrapper ────────────────────────────────────────────────────── */
    .agenda-calendar-schedule {
        --row-height: 6.625rem;
        --column-width: 15rem;
        background: var(--pink);
    }

    /* All-days mode */
    .agenda-calendar-schedule.all {
        display: flex;
        flex-direction: column;
        width: 100%;
        overflow-x: scroll;
        scroll-snap-type: x mandatory;
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .agenda-calendar-schedule.all::-webkit-scrollbar {
        display: none;
    }

    .agenda-calendar-schedule.all .agenda-cal-stage-tracks {
        display: flex;
        flex-direction: row;
    }

    .agenda-calendar-schedule.all .agenda-cal-stage-tracks h3 {
        border-bottom: 1px solid color-mix(in srgb, var(--dark-pink) 30%, transparent);
    }

    .agenda-calendar-schedule.all .agenda-cal-stage-tracks li {
        min-width: var(--column-width);
        border-right: 1px solid color-mix(in srgb, var(--dark-pink) 30%, transparent);
        scroll-snap-align: start;
    }

    .agenda-calendar-schedule.all .agenda-cal-stage-tracks li:last-child {
        border-right: none;
    }

    .agenda-calendar-schedule.all .agenda-cal-stage-tracks li .agenda-cal-card {
        z-index: 1;
        margin: 0;
        width: 100%;
        border-radius: 0;
        border-bottom: 1px solid color-mix(in srgb, var(--dark-pink) 30%, transparent);
    }

    .agenda-calendar-schedule.all .agenda-cal-stage-tracks li .agenda-cal-card:last-child {
        border-bottom: none;
    }

    /* Day mode */
    .agenda-calendar-schedule.day {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .agenda-calendar-schedule.day .agenda-calendar-body {
        display: flex;
        flex-direction: row;
        width: 100%;
        /* Positioning context for the Find a Session overlay, which spans the
           whole body (gutter + tracks) rather than living inside the scrolling
           tracks grid. */
        position: relative;
    }

    /* Phantom copy of the all-day bar sits in the timeline column at the same
       grid-row so the row auto-sizes match between timeline and tracks (both
       are flex children stretched to the same height). Hidden visually but
       occupies the same layout space. */
    .agenda-calendar-schedule.day .agenda-cal-all-day-phantom {
        grid-row: 2;
        grid-column: 1;
        visibility: hidden;
        pointer-events: none;
    }

    /* ── All-day events row (row 2 of tracks grid, spans all columns) ───────── */
    .agenda-calendar-schedule.day .agenda-cal-all-day {
        grid-row: 2;
        grid-column: 1 / span var(--columns);
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 0 0.5rem 0.5rem;
    }
    .agenda-calendar-schedule.day .agenda-cal-all-day-bar {
        display: flex;
        align-items: center;
        gap: 25px;
        padding: 13px 15px;
        background: #fff;
        border-radius: 10px;
        overflow: hidden;
        white-space: nowrap;
        text-decoration: none;
        cursor: pointer;
        transition: background-color 0.15s ease;
    }
    .agenda-calendar-schedule.day .agenda-cal-all-day-bar:hover {
        background: color-mix(in srgb, #fff 92%, var(--dark-pink) 8%);
    }
    .agenda-calendar-schedule.day .agenda-cal-all-day-bar .time {
        font-family: var(--font-hubspot-sans);
        font-weight: 400;
        font-size: 13px;
        line-height: 1.5;
        letter-spacing: 0.195px;
        color: var(--dark-pink);
    }
    .agenda-calendar-schedule.day .agenda-cal-all-day-bar .title {
        font-family: var(--font-hubspot-serif);
        font-weight: 400;
        font-size: 18px;
        line-height: 1.3;
        letter-spacing: 0.09px;
        color: var(--magenta);
    }

    .agenda-calendar-schedule.day .agenda-calendar-timeline {
        display: grid;
        grid-template-rows: 5.1875rem auto repeat(var(--rows), var(--row-height));
        grid-template-columns: repeat(1, 7.625rem);
        width: 7.625rem;
        flex-shrink: 0;
    }

    /* Carousel controls sit in row 1 of the timeline column (the corner cell
       above the time gutter, aligned with the stage-header row). */
    .agenda-calendar-schedule.day .agenda-cal-carousel-controls {
        grid-row: 1;
        grid-column: 1;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .agenda-calendar-schedule.day .agenda-cal-time-label {
        grid-row: var(--starts) / span 4;
        font-family: var(--font-hubspot-sans);
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1;
        letter-spacing: 0.07rem;
        text-transform: uppercase;
        color: var(--magenta);
        border-top: 1px solid var(--dark-pink);
        padding: 0.8125rem 0 0 0;
    }

    .agenda-calendar-schedule.day .agenda-calendar-tracks {
        display: grid;
        grid-template-rows: 5.1875rem auto repeat(var(--rows), var(--row-height));
        grid-template-columns: repeat(var(--columns), minmax(var(--column-width), 1fr));
        column-gap: 0;
        /* Fill the width left by the fixed time gutter so the minmax(,1fr)
           columns stretch: with few columns they share 100% (1 col = full
           width, 2 = 50% each …); with many, each holds --column-width and the
           grid scrolls (min-width:0 lets it shrink to the flex width). */
        flex: 1;
        min-width: 0;
        overflow-x: scroll;
        overflow-y: hidden;
        /* `proximity` (not `mandatory`) so vertical page-scroll over the grid
           doesn't get hijacked into snapping the horizontal axis — the jittery
           "scrolls up/down slightly" feel Nick reported. */
        scroll-snap-type: x proximity;
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .agenda-calendar-schedule.day .agenda-calendar-tracks::-webkit-scrollbar {
        display: none;
    }

    /* My Schedule calendar has no stages/locations, so the stage-header row is
       just an empty band (UNB-588). Drop the sticky stage header and the in-grid
       track-name labels.

       A multi-lane conflict day is the exception: it has real columns to scroll
       between, so it uses the Agenda's header row as-is (Jack, 2026-08-14) —
       sticky behaviour, carousel placement and spacing all come for free and
       match the sessions calendar. The lanes just have no names to put in it. */
    .agenda-calendar-schedule.day.is-myschedule.is-single-lane .agenda-calendar-sticky {
        display: none;
    }

    .agenda-calendar-schedule.day.is-myschedule .agenda-calendar-track-name {
        display: none;
    }

    /* Header row goes to ZERO, not 60px (UNB-591, second pass). The first pass
       shrank it 5.1875rem → 60px reading "this space should be 60px" as the row's
       height, but the row is the labels' band and the labels are gone — leaving it
       at 60px kept a remnant of them, stacked UNDER the action bar's own 40px, so
       the controls sat 100px off the first time row.
       The comp (4678:80134) has no header row at all: its controls end at y=766 and
       the grid's first line starts at y=826 — 60px of pure spacing. The action bar
       contributes 40px of that (shared with the Details/Overview views, so it stays
       put); the 20px below makes up the difference. Measured: 60px, controls to
       first line.
       row_heights is measured from real layout, so the gap blocks follow this
       automatically — don't hand-adjust their offsets to match. */
    .agenda-calendar-schedule.day.is-myschedule .agenda-calendar-timeline,
    .agenda-calendar-schedule.day.is-myschedule .agenda-calendar-tracks {
        grid-template-rows: 0 auto repeat(var(--rows), var(--row-height));
    }

    /* …and it stays at zero on a multi-lane conflict day too, unless the carousel
       arrows are actually on screen.

       This used to restore the Agenda's full 5.1875rem (83px) header row for any
       multi-lane day so the arrows had somewhere to sit. But the arrows only render
       when the grid can scroll, and the row is 83px whether they are there or not —
       so a multi-lane day on a wide viewport reserved 83px for nothing, which is the
       gap in UNB-674.

       When the arrows ARE on screen the row is 3.875rem (62px), not the Agenda
       calendar's 5.1875rem (83px) — UNB-674, third pass. The 83px was chosen to match
       that view exactly, but it seats the 42px pill CENTRED, which spends 20px above
       the pill and 21px below. On the Agenda those 83px are earned by the stage
       labels; My Schedule has none, so on a narrow viewport — the only place the
       arrows appear, since a wider one fits the grid and drops them — it read as a
       gulf between the action links and the arrows.

       Measured at 430px: share-link bottom to pill top was 40px (action-bar margin)
       + 20px (.agenda-calendar-schedule padding-top) + 20.5px (dead space above the
       centred pill) = 80.5px. Pinning the pill to the top of the row removes that
       last 20.5px and lands the pill at exactly 60px below the links — the same
       action-bar-to-next-element rhythm the comp specifies (see the note above), and
       the same 60px desktop already gets, where the row collapses to zero.

       62px = the 42px pill plus 20px beneath it, so the gap under the arrows matches
       the gap above them. */
    .agenda-calendar-schedule.day.is-myschedule.has-carousel .agenda-calendar-timeline,
    .agenda-calendar-schedule.day.is-myschedule.has-carousel .agenda-calendar-tracks {
        grid-template-rows: 3.875rem auto repeat(var(--rows), var(--row-height));
    }

    /* Top, not centre — this is what actually closes the gap. The shared rule centres
       the pill in the row because on the Agenda it lines up with the stage labels
       beside it; here there is nothing to line up with.

       IN-GRID ONLY. The sticky bar reuses this class, and pinning the pill to the
       top there jams it against the bar's top edge with all 20px of slack below —
       which is what it looked like as you scrolled (Jack, 2026-08-21). The sticky
       bar has no links above it to close a gap against, so it keeps the pill
       centred: 62px row less the 42px pill leaves 10px above and 10px below. */
    .agenda-calendar-schedule.day.is-myschedule .agenda-cal-carousel-controls {
        align-items: flex-start;
    }

    .agenda-calendar-schedule.day.is-myschedule .agenda-calendar-sticky .agenda-cal-carousel-controls {
        align-items: center;
    }

    /* The sticky bar mirrors that row, so it has to shrink with it or it would
       reserve 83px of pink for a 42px pill as you scroll. */
    .agenda-calendar-schedule.day.is-myschedule.has-carousel .agenda-calendar-sticky {
        height: 3.875rem;
    }

    /* My Schedule is ONE column with nothing to scroll sideways to, so the shared
       240px column minimum is wrong here. Measured at 375px: the gutter takes
       122px of 327px of content, leaving 228px, and the column holds its 240px
       minimum anyway — 12px of overflow. That overflow is enough to switch on the
       carousel arrows, which render only when the grid can scroll and then sit in
       the corner cell above the time gutter pointing at nothing (UNB). Letting the
       column flex removes the overflow, and the arrows drop out on their own — no
       separate fix needed for them.

       Scoped to .is-myschedule on purpose: the sessions calendar has a column per
       stage and is SUPPOSED to scroll, so it keeps the 240px minimum.

       Also scoped to .is-single-lane (UNB): once a conflict splits the day into
       two lanes there IS somewhere to scroll to, and squeezing both into 228px
       gave two ~114px columns of unreadable cards. Multi-lane days fall through
       to the shared rule above, so each lane holds --column-width, the grid
       scrolls, and the carousel arrows come back on their own — which is the
       peek-at-the-second-column behaviour the ticket asked for. */
    .agenda-calendar-schedule.day.is-myschedule.is-single-lane .agenda-calendar-tracks {
        grid-template-columns: repeat(var(--columns), minmax(0, 1fr));
    }

    /* And give the cards the room back. 7.625rem of gutter is a third of a small
       phone; the time labels ("10:00 AM" at 14px) need nowhere near it. 5.5rem
       leaves 239px for the card column at 375px, which is also what stops the
       Fill Time Slot buttons being cramped inside the Find a Session block. */
    @media (max-width: 767px) {
        .agenda-calendar-schedule.day.is-myschedule .agenda-calendar-timeline {
            width: 5.5rem;
            grid-template-columns: repeat(1, 5.5rem);
        }
    }

    /* Padding, not margin: a margin here adjoins the action bar's own 40px
       margin-bottom and the two collapse to max(40, 20) = 40, so the space never
       appears. Padding can't collapse. It sits outside the body that the gap
       blocks are positioned against, so their offsets are unaffected. */
    .agenda-calendar-schedule.day.is-myschedule {
        padding-top: 1.25rem;
    }

    /* Card grid placement in day mode */
    .agenda-calendar-schedule.day .agenda-cal-card {
        z-index: 1;
        grid-row: var(--starts) / span var(--spans);
        grid-column: var(--index);
        /* height:auto neutralises the card component's own `h-full`. With the
           forced 100% height the card overflowed its grid area and the vertical
           margin below did nothing, so back-to-back cards butted together and
           hid the 15-minute divider line between them (Asana 687600). Letting
           the grid item stretch to its area minus the margin gives a real gap
           at each card boundary, so the divider line shows through. */
        height: auto;
        /* 1px top / 0 bottom (Asana 687600). Nick wants the card flush against the
           15-minute divider with NO pink gap either side — the divider line alone
           separates stacked cards. The divider (.agenda-cal-row-line, --dark-pink)
           is a 1px line at z-index 0, painted in the FIRST pixel of each row; the
           card is z-index 1, so it paints OVER the line. So:
             • top 1px: clears the card off its own top-boundary line so the line
               shows, with the card flush immediately below it (no pink).
             • bottom 0: the next row's line sits just past the card's bottom edge,
               so the card ends flush against it (no pink).
           Result at every boundary: card | 1px line | card, zero pink. Earlier
           rounds used 2px/1px, which left a designed 1px of pink each side — the
           gap Nick kept flagging. Gap blocks match (gapBlockStyle, +1/-1). */
        margin: 0.0625rem 0.5rem 0;
    }

    /* Lift the hovered card above its neighbours so its glow shadow isn't cut off
       where two cards butt together (the day grid overlaps cards edge-to-edge).
       Higher specificity than the base rule above, so it wins the z-index on hover. */
    .agenda-calendar-schedule.day .agenda-cal-card:hover {
        z-index: 2;
    }

    /* Row background boxes */
    .agenda-calendar-schedule.day .agenda-cal-row-box {
        position: relative;
        z-index: 0;
        grid-row: 1 / span calc(var(--rows) + 2);
        grid-column: var(--index);
        background: transparent;
        scroll-snap-align: start;
    }

    .agenda-calendar-schedule.day .agenda-cal-row-box.alt {
        background: #f8b7d3;
    }

    /* The tint spans from row 1 so it reaches up behind the header band — on the
       Agenda calendar that band holds the stage names, and colouring it is what makes
       each column read as one continuous strip.

       My Schedule hides the stage names, so there is no name for that colour to sit
       behind: it just paints a block above the first time stroke (UNB-674). Start it
       at the first time row there instead. This matters even when the header row is
       present for the carousel arrows — the arrows need the row, but an empty column
       header shouldn't be tinted. */
    .agenda-calendar-schedule.day.is-myschedule .agenda-cal-row-box {
        grid-row: 3 / span var(--rows);
    }

    /* Horizontal row lines */
    .agenda-calendar-schedule.day .agenda-cal-row-line {
        position: relative;
        z-index: 0;
        grid-row: var(--starts);
        grid-column: 1 / span var(--columns);
        height: 1px;
        background: #dc95be;
    }

    .agenda-calendar-schedule.day .agenda-cal-row-line.hour {
        background: var(--dark-pink);
    }

    /* ── Track name headers ──────────────────────────────────────────────────── */
    .agenda-calendar-schedule .agenda-calendar-track-name {
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        z-index: 1;
        text-wrap: balance;
        text-align: center;
        font-family: var(--font-hubspot-sans);
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1;
        letter-spacing: 0.07rem;
        text-transform: uppercase;
        color: var(--magenta);
        padding: 1.96875rem 0.875rem 1.71875rem 0.875rem;
        height: 5.1875rem;
        margin: 0;
        grid-row: 1;
        grid-column: var(--index);
    }

    .agenda-calendar-schedule .agenda-calendar-track-name span {
        padding-top: 0.375rem;
        display: block;
        color: var(--dark-pink);
        text-align: center;
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.125rem;
        letter-spacing: 0.009375rem;
        text-transform: none;
    }

    /* Track alternating background — match the day-view column box (.alt = #f8b7d3)
       so the stage-header strip doesn't read a slightly different pink. */
    .agenda-calendar-schedule .agenda-calendar-track:nth-child(even) {
        background: #f8b7d3;
    }

    /* ── Sticky header ───────────────────────────────────────────────────────── */
    .agenda-calendar-sticky {
        z-index: 40;
        position: fixed;
        /* `--cal-sticky-top` is driven from JS to follow the 2026 site header:
           it equals the header's visible bottom edge while the header is on
           screen (scroll-up), and 0 when the header slides away (scroll-down).
           Keeps the calendar header tucked under the nav with no overlap. */
        top: var(--cal-sticky-top, 0px);
        /* Left + width are driven from JS to overlay the calendar grid exactly.
           The grid bleeds to the right viewport edge, so the previous centered
           max-width box left the right-hand columns uncovered. */
        left: var(--cal-sticky-left, 0px);
        width: var(--cal-sticky-width, 90dvw);
        margin: 0;
        display: flex;
        height: 5.1875rem;
        background: var(--pink);
        overflow: hidden;
        border-bottom: 1px solid color-mix(in srgb, var(--dark-pink) 40%, transparent);
        border-right: 1px solid color-mix(in srgb, var(--dark-pink) 30%, transparent);
        transform: translateY(-100%);
        will-change: transform, opacity;
        transition: transform 500ms ease-in-out, top 300ms ease-in-out;
        pointer-events: none;
    }

    .agenda-calendar-sticky .agenda-calendar-tracks {
        pointer-events: none;
        /* Fill the available width like the body's track grid so the
           minmax(--column-width, 1fr) columns stretch identically. Without
           this the header grid shrinks to its 240px content width and the
           sticky-header columns drift left of the (stretched) body columns
           when there are few rooms on a wide viewport. min-width:0 keeps it at
           the flex width (scrolling) when there are many columns. */
        flex: 1;
        min-width: 0;
    }

    .agenda-calendar-sticky .agenda-calendar-track {
        scroll-snap-align: start;
    }

    .agenda-calendar-sticky .agenda-calendar-track:nth-child(odd) {
        background: var(--pink);
    }

    .agenda-calendar-sticky .agenda-calendar-track:nth-child(even) {
        background: #f8b7d3;
    }

    /* Stay stuck whenever scrolled past the grid header, regardless of scroll
       direction (previously only revealed while scrolling down). */
    .agenda-calendar-sticky.active {
        transform: translateY(0);
        pointer-events: all;
    }

    /* ── Time label show/hide ──────────────────────────────────────────────────
       Don't reuse `.desktop` / `.mobile` here — global SCSS owns those class
       names with a 1024px breakpoint and would hide the desktop label at
       tablet widths. */
    .agenda-cal-time-label.short {
        display: none;
    }

    @media (max-width: 767px) {
        .agenda-cal-time-label.long {
            display: none;
        }
        .agenda-cal-time-label.short {
            display: block;
        }
    }

    /* ── Short-session title truncation ─────────────────────────────────────── */
    .session-short .agenda-cal-card p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* ── Gap block ("Find a Session") ────────────────────────────────────────
       Calendar view + My Schedule tab only (spec decision 17). It spans the
       calendar's padded content box MINUS the time-gutter column — i.e. it
       starts where the session tracks begin and runs to the right content edge
       (respecting the right padding, not bleeding to the viewport edge). It's an
       absolute overlay on `.agenda-calendar-body` rather than a tracks-grid
       child: overflow-x: scroll on the tracks grid would clip it and it would
       bleed past the right padding. gapBlockStyle() sets top/height from the grid
       rows the gap covers and left/width from syncGapBlocks(). The body doesn't
       scroll (only the tracks grid scrolls internally), so it holds its
       footprint without position: sticky. */
    .agenda-calendar-schedule.day .agenda-cal-gap-block {
        /* Absolute overlay on the body (gutter + tracks). top/height/left/width
           are set inline from gapBlockStyle(): vertical extent from the grid rows
           the gap covers, horizontal extent from the padded content box. z-index
           sits above the grid cells + time labels but below the fixed sticky
           column header (z-40). */
        z-index: 3;
        position: absolute;
        display: flex;
        flex-direction: column;
        align-items: center;
        /* Not `center`: centering stranded the text + buttons in the middle of a
           tall gap — 509px down a 1166px block — so you had to scroll to find
           them (Asana 687639, second pass). Top-aligned, they're there as soon as
           the gap is. */
        justify-content: flex-start;
        text-align: center;
        /* The 80px top/bottom is the inner's travel envelope for a TALL gap, and
           it's symmetric on purpose: the inner rests 80px below the gap's top, and
           because sticky is bounded by this padding, it releases 80px above the
           gap's bottom. Gaps of an hour or less opt out of sticky and centre
           instead (.is-short, Asana 687639) — the padding still frames them. */
        padding: 80px 20px;
        /* Figma 4583:183787: bg HubSpot-Pink, 1px Darker Pink stroke, 10px radius.
           Opaque (not `transparent`) so the grid's alternating column tint
           (#f8b7d3) doesn't stripe through it. */
        background: var(--pink);
        border: 1px solid #7d4566;
        border-radius: 10px;
    }

    /* Sticky inner (Asana 687639, first pass). Once the gap's top scrolls away the
       wrapper pins `top` below the fixed 2026 header (116px, so 130px clears it
       when it reappears on scroll-up) and stays put until the gap's bottom scrolls
       past — keeping the text + buttons in view the whole time. Sticky is bounded
       by the block's own height, so it never escapes its open region. */
    .agenda-cal-gap-block .agenda-cal-gap-inner {
        position: sticky;
        top: 130px;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    /* Short gap (<= 1 hour, Asana 687639): centre the content and drop sticky —
       the band is too short for sticky to read as anything but a twitch. */
    /* Same prefix as the base rule for the same reason as .is-tight below: at
       0,2,0 this lost to the base rule's `justify-content: flex-start` at 0,3,0,
       so short gaps have never actually centred. The sticky-off rule under it
       does win, being 0,3,0 itself, which is why only half of this pair worked. */
    .agenda-calendar-schedule.day .agenda-cal-gap-block.is-short {
        justify-content: center;
    }
    .agenda-cal-gap-block.is-short .agenda-cal-gap-inner {
        position: static;
        top: auto;
    }

    /* Tight gap: too short for the 80px framing padding, so the content was
       spilling past the block and over the row below it. Trade the padding for
       the content — it still reads as centred because .is-short already centres
       anything an hour or under, and every tight block is by definition short. */
    /* Must carry the same `.agenda-calendar-schedule.day` prefix as the base rule
       above. Without it this is 0,2,0 against the base rule's 0,3,0 and loses:
       the class landed on the element, overflow-hidden applied because the base
       does not set it, and the padding silently did not — so the content stayed
       too tall and simply got clipped instead. */
    .agenda-calendar-schedule.day .agenda-cal-gap-block.is-tight {
        /* ~150px of content in a 210px two-row block: the padding is what has to
           give, and dropping it to 16px leaves the title, subhead and both buttons
           intact. overflow-hidden is the backstop, so a wrapped line can never
           spill onto the row below again. */
        padding: 16px 20px;
        overflow: hidden;
    }

    .agenda-cal-gap-block .agenda-cal-gap-title {
        margin: 0 0 11px;
        font-family: var(--font-hubspot-serif);
        font-weight: 400;
        font-size: 36px;
        line-height: 1.2;
        color: var(--magenta);
    }

    .agenda-cal-gap-block .agenda-cal-gap-subhead {
        margin: 0 0 22px;
        /* No max-width: the gap block is wide on desktop, and a cap here forced this
           one-line subhead to wrap (UNB-587). A narrow block still wraps it to fit. */
        font-family: var(--font-hubspot-sans);
        font-weight: 300;
        font-size: 16px;
        line-height: 26px;
        letter-spacing: 0.32px;
        /* Figma 4583:183806 — HubSpot-Magenta, not Darker Pink. */
        color: var(--magenta);
    }

    .agenda-cal-gap-block .agenda-cal-gap-actions {
        display: flex;
        align-items: center;
        gap: 20px;
    }

    /* Figma 4583:183791 / 4583:183799 — both buttons are Darker Pink (#7d4566),
       NOT magenta. Auto-width from `px-[20px] py-[10px]`, not a fixed 202/246px:
       those numbers were the comp's *rendered* widths, not authored constraints. */
    .agenda-cal-gap-block .agenda-cal-gap-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 10px 20px;
        border-radius: 50px;
        border: 1px solid #7d4566;
        font-family: var(--font-hubspot-serif);
        font-size: 17px;
        line-height: 1.4;
        letter-spacing: 0.34px;
        white-space: nowrap;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
    }

    .agenda-cal-gap-block .agenda-cal-gap-icon {
        width: 13px;
        height: 13px;
        flex-shrink: 0;
    }

    /* `is-outline`, NOT `outline` (Asana 686). `outline` is a Tailwind utility —
       naming the variant that applied `outline-style: solid` at currentColor and
       zero offset, drawing a second ring flush against the button's own 1px
       border. Identical colour, so it read as one 2px stroke and the CSS here
       looked innocent. `is-filled` renamed to match; same trap as the `block`
       utility this project already avoids. Don't rename these back. */
    .agenda-cal-gap-block .agenda-cal-gap-btn.is-outline {
        background: transparent;
        color: #7d4566;
    }

    .agenda-cal-gap-block .agenda-cal-gap-btn.is-filled {
        background: #7d4566;
        color: #fff;
    }

    /* Figma has no hover state for these two buttons (4583:183791 / 4583:183799).
       Carrying the project convention Jack set for the Build My Schedule button:
       "Hover color should be our magenta." Flagged so it can be corrected if a
       hover variant is ever added to the comp. */
    @media (hover: hover) {
        .agenda-cal-gap-block .agenda-cal-gap-btn.is-outline:hover {
            background: var(--magenta);
            border-color: var(--magenta);
            color: #fff;
        }

        .agenda-cal-gap-block .agenda-cal-gap-btn.is-filled:hover {
            background: var(--magenta);
            border-color: var(--magenta);
        }
    }

    @media (max-width: 767px) {
        .agenda-cal-gap-block .agenda-cal-gap-actions {
            flex-direction: column;
            gap: 12px;
        }

        .agenda-cal-gap-block .agenda-cal-gap-btn,
        .agenda-cal-gap-block .agenda-cal-gap-btn.is-outline {
            width: 100%;
            max-width: 246px;
        }
    }

    /* Extra-small phones (UNB, iPhone SE). The gap block is only as wide as the
       calendar's content box minus the time gutter — 223px at 375px — while the
       buttons were holding their 246px cap, so they broke out of the block on
       both sides, and the stack was ~40px taller than the shortest gap could
       hold (the .is-tight backstop then clipped it).
       The 420px breakpoint is where the arithmetic turns: block content width is
       roughly viewport - 176px, so 246px stops fitting just above it.
       Per the ticket the type and buttons scale down here and the AI sparkle is
       dropped outright — there isn't room for it, and it carries no meaning the
       button label doesn't already. */
    @media (max-width: 420px) {
        .agenda-cal-gap-block .agenda-cal-gap-title {
            font-size: 24px;
            margin-bottom: 8px;
        }

        .agenda-cal-gap-block .agenda-cal-gap-subhead {
            font-size: 14px;
            line-height: 20px;
            letter-spacing: 0.28px;
            margin-bottom: 14px;
        }

        .agenda-cal-gap-block .agenda-cal-gap-actions {
            gap: 10px;
            width: 100%;
        }

        /* Cap removed, not just lowered: the button should track the block's own
           width here rather than a fixed number that can exceed it again. */
        .agenda-cal-gap-block .agenda-cal-gap-btn,
        .agenda-cal-gap-block .agenda-cal-gap-btn.is-outline {
            max-width: none;
            padding: 8px 12px;
            font-size: 13px;
            letter-spacing: 0.26px;
        }

        .agenda-cal-gap-block .agenda-cal-gap-icon {
            display: none;
        }

        /* The 80px framing padding is the sticky travel envelope; at this width
           it costs more than the content it frames. */
        .agenda-calendar-schedule.day .agenda-cal-gap-block {
            padding: 32px 16px;
        }

        .agenda-calendar-schedule.day .agenda-cal-gap-block.is-tight {
            padding: 12px 16px;
        }
    }
</style>
